// Embedded-session auth for the Graphy web app.
//
// Graphy runs inside an iframe on the VisionHammer platform. The platform owns
// the customer's session (a JWT access token); this app is a DIFFERENT origin
// and cannot read the platform's storage. The parent relays the current token
// to this frame over `postMessage`, origin-pinned to `VITE_PARENT_ORIGIN`, and
// this module hands it to the API client as a Bearer token.
//
// Security properties:
// - Origin-pinned: only messages whose `event.origin` equals the configured
//   parent origin are accepted; a page in any other frame cannot inject a token.
// - In-memory only: the token is never written to localStorage/sessionStorage,
//   so it is not exposed to this origin's storage or to other scripts through it.
// - Never in a URL: the handshake and the relay are postMessage-only, so the
//   token never lands in a browser/proxy request log.

export interface EmbeddedAuth {
  /** The current Bearer token, or null until the parent has relayed one. */
  getToken(): string | null;
  /** Subscribe to token arrivals/changes; returns an unsubscribe fn. */
  subscribe(cb: (token: string | null) => void): () => void;
  /** Ask the parent to (re)send the current token, e.g. after a 401. */
  requestToken(): void;
}

type OutMsg = { type: "vh-graphy-ready" } | { type: "vh-graphy-auth-request" };

export function createEmbeddedAuth(parentOrigin: string): EmbeddedAuth {
  let token: string | null = null;
  const subs = new Set<(t: string | null) => void>();

  const post = (msg: OutMsg) => {
    try {
      // Not embedded (opened standalone) => window.parent === window; the
      // message is harmless and the "connecting" state handles the no-token case.
      window.parent?.postMessage(msg, parentOrigin);
    } catch {
      /* cross-origin parent access can throw in odd embeddings; stay unauth. */
    }
  };

  window.addEventListener("message", (e: MessageEvent) => {
    if (e.origin !== parentOrigin) return; // origin-pinned: ignore everyone else
    const data = e.data as { type?: string; token?: unknown } | undefined;
    if (!data || data.type !== "vh-graphy-auth") return;
    const next = typeof data.token === "string" && data.token ? data.token : null;
    if (!next || next === token) return;
    token = next;
    for (const cb of subs) cb(token);
  });

  // Handshake: announce readiness so the parent relays the token immediately,
  // instead of us waiting on its first unsolicited push.
  post({ type: "vh-graphy-ready" });

  return {
    getToken: () => token,
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
    requestToken: () => post({ type: "vh-graphy-auth-request" }),
  };
}
